import { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
  BarChart, Bar,
  AreaChart, Area,
} from 'recharts';
import api, { type StatsData, type Course } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { currencySymbol } from '../constants/currency';
import ProGate from '../components/ProGate';
import {
  IconGlobe, IconCalendar, IconBarChart, IconChart, IconMoney, IconGroup,
  IconBook, IconTrendUp, IconCreditCard, IconCash, IconPresentCheck,
  IconClockLate, IconCloseX, IconMedal, IconPercent, IconUsers,
} from '../components/Icons';

// ─── Constants ─────────────────────────────────────────────────────────────

const PERIODS = [
  { key: 'today', label: 'Сегодня', icon: 'today' },
  { key: 'week', label: 'Неделя', icon: 'week' },
  { key: 'month', label: 'Месяц', icon: 'month' },
  { key: 'all', label: 'Всё время', icon: 'all' },
] as const;

function PeriodIcon({ type }: { type: string }) {
  const cls = 'w-3.5 h-3.5 inline';
  switch (type) {
    case 'today': return <IconGlobe className={cls} />;
    case 'week': case 'month': return <IconCalendar className={cls} />;
    case 'all': return <IconBarChart className={cls} />;
    default: return null;
  }
}

const CHART_COLORS = ['#6C5CE7', '#00B894', '#FD79A8', '#FDCB6E', '#E17055', '#0984E3', '#00CEC9', '#636E72'];

const MONTH_NAMES: Record<string, string> = {
  '01': 'Янв', '02': 'Фев', '03': 'Мар', '04': 'Апр',
  '05': 'Май', '06': 'Июн', '07': 'Июл', '08': 'Авг',
  '09': 'Сен', '10': 'Окт', '11': 'Ноя', '12': 'Дек',
};

// ─── Formatters ────────────────────────────────────────────────────────────

function formatMonth(monthKey: string): string {
  const parts = monthKey.split('-');
  if (parts.length === 2) {
    return `${MONTH_NAMES[parts[1]] || parts[1]} ${parts[0]}`;
  }
  return monthKey;
}

function formatShortDate(dateStr: string): string {
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parseInt(parts[2])}.${parts[1]}`;
  }
  return dateStr;
}

function formatCurrency(amount: number): string {
  if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `${(amount / 1_000).toFixed(0)}k`;
  return amount.toString();
}

function formatFullCurrency(amount: number, symbol: string): string {
  return amount.toLocaleString('ru-RU') + ' ' + symbol;
}

// ─── Tooltip ───────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label, formatter }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[var(--tg-theme-section-bg-color)] border border-[var(--tg-theme-section-separator-color)] rounded-xl px-3 py-2 shadow-lg text-xs">
      <p className="font-medium text-[var(--tg-theme-text-color)] mb-1">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} style={{ color: entry.color }} className="flex items-center gap-1">
          {formatter ? formatter(entry.value, entry.name) : `${entry.name}: ${entry.value}`}
        </p>
      ))}
    </div>
  );
}

// ─── Change badge ──────────────────────────────────────────────────────────

function ChangeBadge({ change }: { change: number | null | undefined }) {
  if (change === null || change === undefined) return null;
  const isGood = change >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold ml-1.5
      ${isGood ? 'text-green-500' : 'text-red-500'}`}>
      {isGood ? '▲' : '▼'} {Math.abs(change)}%
    </span>
  );
}

// ─── KPI Card ──────────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, change, sub, color, onClick }: {
  icon: string; label: string; value: string | number;
  change?: number | null; sub?: string; color?: string; onClick?: () => void;
}) {
  const Comp = onClick ? 'button' : 'div';
  const iconMap: Record<string, React.ReactNode> = {
    chart: <IconChart className="w-5 h-5" />,
    money: <IconMoney className="w-5 h-5" />,
    group: <IconGroup className="w-5 h-5" />,
    book: <IconBook className="w-5 h-5" />,
  };
  return (
    <Comp onClick={onClick}
      className={`bg-[var(--tg-theme-section-bg-color)] rounded-2xl p-4 shadow-sm
        transition-all duration-200 hover:shadow-md hover:-translate-y-0.5
        ${onClick ? 'active:scale-[0.98] cursor-pointer' : ''}
        text-left w-full`}>
      <div className="flex items-center gap-2 mb-1.5">
        {iconMap[icon] || <span className="text-lg">{icon}</span>}
        <span className="text-[11px] text-[var(--tg-theme-hint-color)] font-medium uppercase tracking-wide">
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-0">
        <span className="text-2xl font-bold text-[var(--tg-theme-text-color)]"
              style={color ? { color } : undefined}>
          {value}
        </span>
        <ChangeBadge change={change} />
      </div>
      {sub && (
        <span className="text-[11px] text-[var(--tg-theme-hint-color)] mt-0.5 block">
          {sub}
        </span>
      )}
    </Comp>
  );
}

// ─── Section Header ────────────────────────────────────────────────────────

function SectionHeader({ icon, title, badge }: { icon: string; title: string; badge?: string }) {
  const iconMap: Record<string, React.ReactNode> = {
    trend: <IconTrendUp className="w-4 h-4" />,
    money: <IconMoney className="w-4 h-4" />,
    credit: <IconCreditCard className="w-4 h-4" />,
    medal: <IconMedal className="w-4 h-4" />,
    users: <IconUsers className="w-4 h-4" />,
  };
  return (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] flex items-center gap-1.5">
        {iconMap[icon] || <span>{icon}</span>} {title}
      </h3>
      {badge && (
        <span className="text-[10px] font-medium text-[var(--tg-theme-hint-color)] bg-[var(--tg-theme-secondary-bg-color)] px-2 py-0.5 rounded-full">
          {badge}
        </span>
      )}
    </div>
  );
}

// ─── Card ──────────────────────────────────────────────────────────────────

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-[var(--tg-theme-section-bg-color)] rounded-2xl p-4 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

// ─── Simple Modal ──────────────────────────────────────────────────────────

function Modal({ title, children, onClose }: {
  title: string; children: React.ReactNode; onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative bg-[var(--tg-theme-section-bg-color)] rounded-2xl p-5 shadow-xl
                      max-w-sm w-full animate-fade-in"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[var(--tg-theme-text-color)]">{title}</h3>
          <button onClick={onClose}
                  className="w-7 h-7 rounded-full bg-[var(--tg-theme-secondary-bg-color)]
                             flex items-center justify-center text-sm
                             hover:opacity-80 active:scale-90 transition-all">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Progress bar helper ───────────────────────────────────────────────────

function ProgressBar({ value, color }: { value: number; color: string }) {
  return (
    <div className="h-1.5 bg-[var(--tg-theme-secondary-bg-color)] rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-700"
           style={{ width: `${Math.min(value, 100)}%`, backgroundColor: color }} />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ════════════════════════════════════════════════════════════════════════════

export default function StatisticsPage() {
  const { canUseFeature, user } = useAuth();
  const curSymbol = currencySymbol(user?.currency);
  const [data, setData] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState('month');
  const [courseFilter, setCourseFilter] = useState<string>('');
  const [modal, setModal] = useState<{ title: string; content: React.ReactNode } | null>(null);

  useEffect(() => {
    loadStats(period, courseFilter);
  }, [period, courseFilter]);

  // ── ProGate: расширенная статистика — платная фича (Pro / School) ──────
  // Системный owner и владельцы Pro/School видят всё; Free — замок с CTA.
  if (!canUseFeature('advanced_stats')) {
    return (
      <ProGate
        feature="advanced_stats"
        title="Расширенная статистика"
        description="Графики посещаемости, доходы по курсам и месяцам, топ учеников и рейтинг курсов — доступны в тарифах Pro и School."
      />
    );
  }

  const loadStats = async (p: string, cid?: string) => {
    try {
      setLoading(true);
      setError(null);
      const result = await api.getStats(p, cid || undefined);
      setData(result);
    } catch (e) {
      console.error('Failed to load stats:', e);
      setError('Не удалось загрузить статистику');
    } finally {
      setLoading(false);
    }
  };

  // ── Modal helpers ──────────────────────────────────────────────────────

  const showDayDetail = (day: { date: string; attended: number; total: number }) => {
    setModal({            title: `${formatShortDate(day.date)}`,
      content: (
        <div className="space-y-3">
          <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-[var(--tg-theme-text-color)]">
              {day.attended} / {day.total}
            </div>
            <div className="text-xs text-[var(--tg-theme-hint-color)] mt-1">учеников пришло</div>
          </div>
          <ProgressBar
            value={day.total > 0 ? (day.attended / day.total) * 100 : 0}
            color="#6C5CE7"
          />
          <p className="text-xs text-[var(--tg-theme-hint-color)] text-center">
            {day.total > 0 ? `${Math.round((day.attended / day.total) * 100)}% посещаемость` : 'Нет данных'}
          </p>
        </div>
      ),
    });
  };

  const showMonthDetail = (monthData: { month: string; amount: number }) => {
    setModal({            title: `${formatMonth(monthData.month)}`,
      content: (
        <div className="space-y-3">
          <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-green-500">
              {formatFullCurrency(monthData.amount, curSymbol)}
            </div>
            <div className="text-xs text-[var(--tg-theme-hint-color)] mt-1">доход за месяц</div>
          </div>
        </div>
      ),
    });
  };

  const showCourseRevenue = (courseData: { name: string; value: number; pct: number }) => {
    setModal({            title: `${courseData.name}`,
      content: (
        <div className="space-y-3">
          <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-[var(--tg-theme-text-color)]">
              {formatFullCurrency(courseData.value, curSymbol)}
            </div>
            <div className="text-xs text-[var(--tg-theme-hint-color)] mt-1">доход · {courseData.pct}% от общего</div>
          </div>
        </div>
      ),
    });
  };

  const showStudentDetail = (s: StatsData['top_students'][0]) => {
    setModal({
      title: `👨‍🎓 ${s.name}`,
      content: (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-green-500">{s.rate}%</div>
              <div className="text-[10px] text-[var(--tg-theme-hint-color)] mt-0.5">Посещаемость</div>
            </div>
            <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-[var(--tg-theme-text-color)]">
                {s.attended}/{s.total}
              </div>
              <div className="text-[10px] text-[var(--tg-theme-hint-color)] mt-0.5">Занятий</div>
            </div>
          </div>
        </div>
      ),
    });
  };

  // ── Loading skeleton ───────────────────────────────────────────────────
  if (loading && !data) {
    return (
      <div className="p-4 space-y-4 animate-fade-in">
        <div className="skeleton h-6 w-44 mb-3" />
        <div className="flex gap-2 mb-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-8 w-20 rounded-full" />)}
        </div>
        <div className="skeleton h-9 w-full rounded-xl mb-2" />
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-28 rounded-2xl" />)}
        </div>
        <div className="skeleton h-52 w-full rounded-2xl" />
        <div className="skeleton h-40 w-full rounded-2xl" />
        <div className="skeleton h-40 w-full rounded-2xl" />
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────
  if (error && !data) {
    return (
      <div className="p-4 animate-fade-in">
        <div className="bg-[var(--tg-theme-section-bg-color)] rounded-2xl flex flex-col items-center py-10 text-center shadow-sm">
          <div className="mb-3"><IconBarChart className="w-10 h-10 text-[var(--tg-theme-hint-color)]" /></div>
          <p className="text-sm text-[var(--tg-theme-hint-color)] mb-4">{error}</p>
          <button onClick={() => loadStats(period, courseFilter)}
                  className="px-5 py-2 rounded-full text-sm font-medium
                             bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)]
                             transition-all duration-200 hover:opacity-90 active:scale-95">
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { kpi, attendance, revenue, students, total_lessons, top_students, courses } = data;

  // ── Derived chart data ────────────────────────────────────────────────
  const attendanceChartData = attendance.daily.map(d => ({
    date: formatShortDate(d.date),
    attended: d.attended,
    total: d.total,
    rate: d.total > 0 ? Math.round((d.attended / d.total) * 100) : 0,
  }));

  const revenueMonthlyData = revenue.monthly.map(m => ({
    month: formatMonth(m.month),
    amount: m.amount,
  }));

  // Revenue by course — top 6 + Others
  const revCourseData = revenue.by_course.slice(0, 6).map(c => ({
    name: c.course_title,
    value: c.amount,
    course_id: c.course_id,
    pct: revenue.total > 0 ? Math.round((c.amount / revenue.total) * 100) : 0,
  }));
  const otherAmount = revenue.by_course.slice(6).reduce((s, c) => s + c.amount, 0);
  if (otherAmount > 0) revCourseData.push({ name: 'Другие', value: otherAmount, course_id: '', pct: 0 });

  // Payment methods pie
  const methodEntries = Object.entries(revenue.by_method);
  const methodPieData = methodEntries.map(([name, pct]) => ({ name, value: pct }));

  // Course ranking
  const courseRanking = [...attendance.by_course]
    .filter(c => c.total > 0)
    .sort((a, b) => b.rate - a.rate);

  // Top students
  const displayStudents = top_students.slice(0, 5);

  // ── KPI display values ────────────────────────────────────────────────
  const kpiAttendance = `${kpi.attendance.value}%`;
  const kpiAttendanceSub = `${attendance.present} пришли · ${attendance.late} опоздали · ${attendance.absent} пропустили`;
  const kpiRevenue = formatCurrency(kpi.revenue.value);
  const kpiStudents = kpi.students.value;
  const kpiStudentsSub = `+${kpi.students.new} новых · ${kpi.students.inactive} ушли`;
  const kpiLessons = kpi.lessons.value;
  const kpiLessonsSub = period === 'today' ? 'за сегодня' : `за ${data.period_label.toLowerCase()}`;

  return (
    <div className="p-4 pb-20 space-y-4 animate-fade-in">

      {/* ── Modal ────────────────────────────────────────────────────────── */}
      {modal && (
        <Modal title={modal.title} onClose={() => setModal(null)}>
          {modal.content}
        </Modal>
      )}

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-base font-bold text-[var(--tg-theme-text-color)] flex items-center gap-2">
          <IconBarChart className="w-5 h-5" /> Статистика
        </h2>
        <span className="text-[11px] text-[var(--tg-theme-hint-color)] font-medium">
          {data.period_label}
        </span>
      </div>

      {/* ── Period Selector ─────────────────────────────────────────────── */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
        {PERIODS.map(p => (
          <button key={p.key}
            onClick={() => { setPeriod(p.key); setCourseFilter(''); }}
            disabled={loading}
            className={`px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap
                        transition-all duration-200
                        ${period === p.key
                          ? 'bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] shadow-sm'
                          : 'bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-hint-color)] hover:text-[var(--tg-theme-text-color)]'
                        }
                        ${loading ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}`}>
            <PeriodIcon type={p.icon} /> {p.label}
          </button>
        ))}
      </div>

      {/* ── Course Filter ──────────────────────────────────────────────── */}
      {courses.length > 1 && (
        <div className="relative">
          <select value={courseFilter}
            onChange={e => setCourseFilter(e.target.value)}
            disabled={loading}
            className="w-full px-3.5 py-2.5 rounded-xl text-xs font-medium
                       bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]
                       border-none outline-none appearance-none cursor-pointer
                       transition-all duration-200 hover:opacity-80
                       disabled:opacity-50 disabled:cursor-not-allowed">
            <option value="">📚 Все курсы</option>
            {courses.map(c => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[var(--tg-theme-hint-color)] text-xs">
            ▼
          </div>
        </div>
      )}

      {/* ── Loading overlay ────────────────────────────────────────────── */}
      {loading && data && (
        <div className="flex items-center justify-center py-2">
          <div className="w-4 h-4 border-2 border-[var(--tg-theme-button-color)] border-t-transparent rounded-full animate-spin" />
          <span className="text-xs text-[var(--tg-theme-hint-color)] ml-2">Загрузка...</span>
        </div>
      )}

      {/* ── KPI Cards with Change ──────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        <KpiCard icon="chart" label="Посещаемость"
          value={kpiAttendance}
          change={kpi.attendance.change}
          sub={kpiAttendanceSub}
          color={kpi.attendance.value >= 85 ? '#22c55e' : kpi.attendance.value >= 60 ? '#f59e0b' : '#ef4444'}
        />
        <KpiCard icon="money" label="Доход"
          value={kpiRevenue}
          change={kpi.revenue.change}
          sub={formatFullCurrency(kpi.revenue.value, curSymbol)}
        />
        <KpiCard icon="group" label="Учеников"
          value={kpiStudents}
          sub={kpiStudentsSub}
        />
        <KpiCard icon="book" label="Занятий"
          value={kpiLessons}
          change={kpi.lessons.change}
          sub={kpiLessonsSub}
        />
      </div>

      {/* ── Attendance Trend Chart (clickable) ─────────────────────────── */}
      {attendanceChartData.length > 0 && (
        <Card>
          <SectionHeader icon="trend" title="Посещаемость по дням" badge={`сред. ${kpi.attendance.value}%`} />
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={attendanceChartData}
                margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
                onClick={(e: any) => {
                  if (e?.activePayload?.[0]?.payload) {
                    showDayDetail(e.activePayload[0].payload);
                  }
                }}>
                <defs>
                  <linearGradient id="rateGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6C5CE7" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6C5CE7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--tg-theme-section-separator-color)" opacity={0.4} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--tg-theme-hint-color)' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: 'var(--tg-theme-hint-color)' }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
                <Tooltip content={<ChartTooltip formatter={(v: number, _n: string) => `${v}%`} />} />
                <Area type="monotone" dataKey="rate" stroke="#6C5CE7" strokeWidth={2} fill="url(#rateGrad)"
                  dot={{ r: 2, fill: '#6C5CE7', strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: '#6C5CE7', stroke: 'var(--tg-theme-section-bg-color)', strokeWidth: 2, cursor: 'pointer' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-3 mt-2 text-xs text-[var(--tg-theme-hint-color)]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />{attendance.present} пришли</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />{attendance.late} опоздали</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />{attendance.absent} пропустили</span>
          </div>
          <p className="text-[10px] text-[var(--tg-theme-hint-color)] mt-1.5">💡 Кликните по точке для деталей дня</p>
        </Card>
      )}

      {/* ── Revenue Section ────────────────────────────────────────────── */}
      {(revCourseData.length > 0 || revenueMonthlyData.length > 0) && (
        <Card>
          <SectionHeader icon="money" title="Доходы" badge={formatFullCurrency(kpi.revenue.value, curSymbol)} />

          {/* Revenue by course — clickable pie */}
          {revCourseData.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-2">По курсам</h4>
              <div className="flex items-center gap-4">
                <div className="w-28 h-28 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={revCourseData} cx="50%" cy="50%" innerRadius={28} outerRadius={42}
                        paddingAngle={3} dataKey="value"
                        onClick={(_, i) => showCourseRevenue({ ...revCourseData[i], pct: revCourseData[i].pct })}
                        style={{ cursor: 'pointer' }}>
                        {revCourseData.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip formatter={(v: number) => formatFullCurrency(v, curSymbol)} />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 min-w-0 space-y-1.5">
                  {revCourseData.map((item, i) => (
                    <button key={item.name} onClick={() => showCourseRevenue({ ...item, pct: item.pct })}
                      className="flex items-center justify-between text-xs w-full text-left hover:opacity-80 transition-opacity">
                      <span className="flex items-center gap-1.5 truncate">
                        <span className="w-2 h-2 rounded-full shrink-0"
                              style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                        <span className="truncate text-[var(--tg-theme-text-color)]">{item.name}</span>
                      </span>
                      <span className="font-medium text-[var(--tg-theme-text-color)] shrink-0 ml-2">
                        {formatCurrency(item.value)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Revenue by month — clickable bar chart */}
          {revenueMonthlyData.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-2">По месяцам</h4>
              <div className="h-36">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueMonthlyData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
                    onClick={(e: any) => {
                      if (e?.activePayload?.[0]?.payload) {
                        showMonthDetail(e.activePayload[0].payload);
                      }
                    }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--tg-theme-section-separator-color)" opacity={0.3} />
                    <XAxis dataKey="month" tick={{ fontSize: 9, fill: 'var(--tg-theme-hint-color)' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: 'var(--tg-theme-hint-color)' }} axisLine={false} tickLine={false} tickFormatter={formatCurrency} />
                    <Tooltip content={<ChartTooltip formatter={(v: number) => formatFullCurrency(v, curSymbol)} />} />
                    <Bar dataKey="amount" radius={[4, 4, 0, 0]} maxBarSize={36} style={{ cursor: 'pointer' }}>
                      {revenueMonthlyData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.85} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[10px] text-[var(--tg-theme-hint-color)] mt-1">💡 Кликните по столбцу для деталей</p>
            </div>
          )}
        </Card>
      )}

      {/* ── Payment Methods Pie ─────────────────────────────────────────── */}
      {methodPieData.length > 0 && (
        <Card>
          <SectionHeader icon="credit" title="Способы оплаты" />
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={methodPieData} cx="50%" cy="50%" innerRadius={22} outerRadius={36}
                    paddingAngle={3} dataKey="value">
                    {methodPieData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2">
              {methodEntries.map(([method, pct], i) => (
                <div key={method}>
                  <div className="flex items-center justify-between text-xs mb-0.5">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full shrink-0"
                            style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span className="text-[var(--tg-theme-text-color)]">{method}</span>
                    </span>
                    <span className="font-medium text-[var(--tg-theme-text-color)]">{pct}%</span>
                  </div>
                  <ProgressBar value={pct} color={CHART_COLORS[i % CHART_COLORS.length]} />
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* ── Top Students ──────────────────────────────────────────────── */}
      {displayStudents.length > 0 && (
        <Card>
          <SectionHeader icon="medal" title="Топ учеников" badge="по посещаемости" />
          <div className="space-y-2.5">
            {displayStudents.map((s) => {
              const medal = s.rank === 1 ? '🥇' : s.rank === 2 ? '🥈' : s.rank === 3 ? '🥉' : `${s.rank}.`;
              const color = s.rate >= 85 ? '#22c55e' : s.rate >= 60 ? '#f59e0b' : '#ef4444';
              return (
                <button key={s.student_id} onClick={() => showStudentDetail(s)}
                  className="w-full text-left hover:opacity-80 transition-opacity">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="flex items-center gap-1.5 truncate">
                      <span className="font-medium shrink-0">{medal}</span>
                      <span className="truncate text-[var(--tg-theme-text-color)]">{s.name}</span>
                    </span>
                    <span className="font-medium shrink-0 ml-2" style={{ color }}>{s.rate}%</span>
                  </div>
                  <ProgressBar value={s.rate} color={color} />
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── Student Activity ────────────────────────────────────────────── */}
      <Card>
        <SectionHeader icon="users" title="Активность учеников" />
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-3 transition-colors hover:bg-opacity-80">
            <div className="text-xl font-bold text-green-500">{students.new}</div>
            <div className="text-[10px] text-[var(--tg-theme-hint-color)] mt-0.5">Новых</div>
          </div>
          <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-3 transition-colors hover:bg-opacity-80">
            <div className="text-xl font-bold text-red-500">{students.inactive}</div>
            <div className="text-[10px] text-[var(--tg-theme-hint-color)] mt-0.5">Ушли</div>
          </div>
          <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-3 transition-colors hover:bg-opacity-80">
            <div className="text-xl font-bold text-[var(--tg-theme-text-color)]">{students.retention_rate}%</div>
            <div className="text-[10px] text-[var(--tg-theme-hint-color)] mt-0.5">Retention</div>
          </div>
        </div>
      </Card>

      {/* ── Course Ranking ────────────────────────────────────────────── */}
      {courseRanking.length > 0 && (
        <Card>
          <SectionHeader icon="medal" title="Рейтинг курсов" badge="по посещаемости" />
          <div className="space-y-2.5">
            {courseRanking.map((course, i) => {
              const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`;
              const color = course.rate >= 85 ? '#22c55e' : course.rate >= 60 ? '#f59e0b' : '#ef4444';
              return (
                <div key={course.course_id}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="flex items-center gap-1.5 truncate">
                      <span className="font-medium shrink-0">{medal}</span>
                      <span className="truncate text-[var(--tg-theme-text-color)]">{course.course_title}</span>
                    </span>
                    <span className="font-medium shrink-0 ml-2" style={{ color }}>{course.rate}%</span>
                  </div>
                  <ProgressBar value={course.rate} color={color} />
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── Empty state ─────────────────────────────────────────────────── */}
      {attendance.daily.length === 0 && revenue.by_course.length === 0 && (
        <Card className="text-center py-8">
          <span className="text-4xl block mb-3">📊</span>
          <p className="text-sm text-[var(--tg-theme-hint-color)]">
            {period === 'all' ? 'Нет данных для отображения' : `Нет данных за ${data.period_label.toLowerCase()}`}
          </p>
          <p className="text-xs text-[var(--tg-theme-hint-color)] mt-1">
            Добавьте учеников, отметьте посещаемость и платежи
          </p>
        </Card>
      )}
    </div>
  );
}
